import React, {
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	type ReactNode,
	type ComponentType,
} from 'react';
import flow from 'lodash/flow';
import memoizeOne from 'memoize-one';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useViewMode } from '@atlassian/jira-issue-context-service/src/main.tsx';
import Board from '@atlassian/jira-platform-board-kit/src/ui/board/index.tsx';
import { AnalyticsEventToProps, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { connect } from '@atlassian/jira-react-redux/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { getIsSoftwareThemingEnabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { getIsCMPBoard } from '../../../state/selectors/software/software-selectors.tsx';
import { Capability } from '../../../common/capability/index.tsx';
import { boardView } from '../../../state/actions/work/index.tsx';
import { getCapability } from '../../../state/selectors/capability/capability-selectors.tsx';
import { getIssueKeyForModal } from '../../../state/selectors/issue/issue-modal-selectors.tsx';
import { getInsightsPanel } from '../../../state/selectors/panels/index.tsx';
import type { Dispatch, State } from '../../../state/types.tsx';
import { getDraggingCardId } from '../../../state/selectors/card/card-selectors.tsx';

type BoardProps = ComponentProps<typeof Board>;
type BoardViewCallback = BoardProps['onBoardView'];
type ScrollCallback = BoardProps['onScroll'];

// Need to modify Props since withAnalyticsEvents introduces an analyticsEvent argument as the last
// parameter to onBoardView and onCardsMove callbacks
type BoardPropsWithoutOnBoardView = Flow.Diff<
	BoardProps,
	{
		onBoardView: BoardViewCallback;
	}
>;

// Need to modify Props since withAnalyticsEvents introduces an analyticsEvent argument as the last
// parameter to onScroll callbacks
type BoardPropsWithoutOnBoardViewAndOnScroll = Flow.Diff<
	BoardPropsWithoutOnBoardView,
	{
		onScroll: ScrollCallback;
	}
>;
type Props = BoardPropsWithoutOnBoardViewAndOnScroll & {
	onBoardView: (
		boardInfo: {
			hasHorizontalScrollbar?: boolean;
		},
		analyticsEvent: UIAnalyticsEvent, // eslint-disable-next-line @typescript-eslint/no-explicit-any
	) => any;
	onScroll: (
		boardScrollInfo: {
			reachedLeftEdge: boolean;
			reachedRightEdge: boolean;
		},
		analyticsEvent: UIAnalyticsEvent, // eslint-disable-next-line @typescript-eslint/no-explicit-any
	) => any;
	getIssueKeyForModal: string | null | undefined;
	isInsightsPanelOpen: boolean;
	externalId?: string;
};

type OwnProps = {
	children: ReactNode;
};

export const mapStateToProps = (state: State) => ({
	getIssueKeyForModal: getIssueKeyForModal(state),
	isInsightsPanelOpen: getInsightsPanel(state),
	flexibleColumns: getCapability(state)(Capability.FLEXIBLE_BOARD_COLUMNS),
	showTransitionZones: !!getDraggingCardId(state),
	isCMPBoard: getIsSoftwareThemingEnabled() ? getIsCMPBoard(state) : undefined,
});

export const mapDispatchToProps = (dispatch: Dispatch) => ({
	dispatch,
});

const onBoardViewFactory = memoizeOne(
	(dispatch: Dispatch) =>
		(
			boardInfo: {
				hasHorizontalScrollbar?: boolean;
			},
			analyticsEvent: UIAnalyticsEvent,
		) =>
			dispatch(boardView({ ...boardInfo }, analyticsEvent)),
);
const onScrollFactory = memoizeOne(
	() =>
		(
			boardScrollInfo: {
				reachedLeftEdge: boolean;
				reachedRightEdge: boolean;
			},
			analyticsEvent: UIAnalyticsEvent,
		) => {
			const { reachedLeftEdge, reachedRightEdge } = boardScrollInfo;
			if (reachedLeftEdge || reachedRightEdge) {
				const payload = {
					reachedLeftEdge,
					reachedRightEdge,
				};

				fireUIAnalytics(analyticsEvent, payload);
			}
		},
);

export const mergeProps = (
	stateProps: ReturnType<typeof mapStateToProps>,
	dispatchProps: ReturnType<typeof mapDispatchToProps>,
	ownProps: OwnProps,
): Props => ({
	...ownProps,
	getIssueKeyForModal: stateProps.getIssueKeyForModal,
	isInsightsPanelOpen: stateProps.isInsightsPanelOpen,
	flexibleColumns: stateProps.flexibleColumns,
	showTransitionZones: fg('avoid_board_scroll_container_style_changes')
		? stateProps.showTransitionZones
		: undefined,
	onBoardView: onBoardViewFactory(dispatchProps.dispatch),
	externalId: 'software.board',
	onScroll: onScrollFactory(),
	isCMPBoard: getIsSoftwareThemingEnabled() ? stateProps.isCMPBoard : undefined,
});

const withOffsetRightPaddingByWidth =
	(WrappedComponent: ComponentType<{ offsetRightPaddingByWidth?: string | boolean }>) =>
	(props: Props) => {
		const viewMode = useViewMode();
		const {
			getIssueKeyForModal: getIssueKeyForModalProp,
			isInsightsPanelOpen: isInsightsPanelOpenProp,
			...rest
		} = props;
		const offsetRightPaddingByWidth =
			(viewMode === 'SIDEBAR' && getIssueKeyForModalProp) || isInsightsPanelOpenProp;

		return <WrappedComponent {...rest} offsetRightPaddingByWidth={offsetRightPaddingByWidth} />;
	};

const connector = connect(mapStateToProps, mapDispatchToProps, mergeProps);

export default flow(
	AnalyticsEventToProps('board', {
		onBoardView: 'viewed',
		onScroll: 'scrolled',
	}),
	withOffsetRightPaddingByWidth,
	connector,
)(Board);
